// components
import { Chip, Typography } from '@mui/material';
import { IconButton } from '@sdb/planning-ui';
import ChatMessageBubble from './components/ChatMessageBubble';
import ProgressSteps from './components/ProgressSteps';

// hooks
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAutomaticRosteringExplanation from '../hooks/useAutomaticRosteringExplanation';
import useAutomaticRosteringSuggestions from '../hooks/useAutomaticRosteringSuggestions';

// utils
import {
  isAutomaticRosteringExplainable,
  isAutomaticRosteringInProgress
} from '../automaticRostering.utils';

// types
import type { FormEvent } from 'react';
import type { AutomaticRosteringExplainPanelProps } from './AutomaticRosteringExplainPanel.types';

// styles
import {
  StyledComposer,
  StyledHeader,
  StyledHeaderTitleBox,
  StyledMessageList,
  StyledPanel,
  StyledQuestionField,
  StyledSuggestionsBox
} from './AutomaticRosteringExplainPanel.styles';

const AutomaticRosteringExplainPanel = ({
  run,
  organizationalUnitCode,
  startDate,
  endDate,
  subtitle,
  onClose
}: AutomaticRosteringExplainPanelProps) => {
  'use memo';

  // Right after navigating the panel stays open while the week's status is read, and until then the
  // state is `idle`. The panel waits for that read: a greeting or a question before it would be about
  // a week that may turn out to have no calculation, and the question would cost an engine call whose
  // answer is thrown away once the panel closes.
  const isAwaitingStatus = !isAutomaticRosteringExplainable(run.state);

  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan.automaticRostering'
  });
  const { messages, suggestions, isAnswering, askQuestion } =
    useAutomaticRosteringExplanation({
      organizationalUnitCode,
      startDate,
      endDate
    });
  const {
    suggestions: openingQuestions,
    isAvailable,
    unavailableReason
  } = useAutomaticRosteringSuggestions({
    organizationalUnitCode,
    startDate,
    endDate,
    enabled: !isAwaitingStatus,
    runState: run.state
  });
  const [question, setQuestion] = useState('');
  const isRunning = isAutomaticRosteringInProgress(run.state);
  // Once the panel has followed a calculation it keeps showing the steps it went through, so the
  // planner sees how it ended rather than having them vanish on delivery. A week opened after the
  // fact shows no steps: there all of them are done, and that tells nothing the greeting does not.
  const [hasFollowedRun, setHasFollowedRun] = useState(isRunning);
  if (isRunning && !hasFollowedRun) setHasFollowedRun(true);
  // A calculation that ended on a failed step, or on one that needs attention (a manual review),
  // always shows them: the step it stopped on, with the engine's sentence under it, is the closest
  // thing to a why.
  const hasStoppedOnStep = run.progressSteps.some(
    (step) => step.state === 'FAILED' || step.state === 'ATTENTION'
  );
  const showProgress =
    run.progressSteps.length > 0 &&
    (isRunning || hasFollowedRun || hasStoppedOnStep);
  const isComposerDisabled =
    isAnswering || isRunning || isAwaitingStatus || !organizationalUnitCode;
  const messageListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const list = messageListRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages.length, isAnswering, run.progressSteps.length]);

  const submitQuestion = (value: string) => {
    if (!value.trim() || isAnswering || isRunning || isAwaitingStatus) return;

    askQuestion(value);
    setQuestion('');
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submitQuestion(question);
  };

  // The engine says why it has nothing to explain; the greeting repeats that in the planner's own
  // terms rather than opening with an invitation it cannot honour.
  const greeting =
    !isAvailable && unavailableReason
      ? t(`chat.unavailable.${unavailableReason}`)
      : run.state === 'DELIVERED'
      ? t('chat.greetingDelivered')
      : t('chat.greeting');

  // Only before the first question: after that the engine's follow-ups take over.
  const visibleSuggestions =
    messages.length === 0 ? openingQuestions : suggestions;

  return (
    <StyledPanel elevation={0}>
      <StyledHeader>
        <StyledHeaderTitleBox>
          <Typography variant="subtitle1" component="h2">
            {t('chat.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </StyledHeaderTitleBox>
        <IconButton
          type="xmark"
          size="small"
          onClick={onClose}
          aria-label={t('chat.close')}
        />
      </StyledHeader>

      <StyledMessageList ref={messageListRef} role="log" aria-live="polite">
        {/* While it calculates the panel says so above the steps; once it is done the steps are
            the history and the greeting follows them. */}
        {isRunning && (
          <ChatMessageBubble role="assistant" text={t('chat.progress.intro')} />
        )}
        {showProgress && (
          <ProgressSteps
            steps={run.progressSteps}
            message={hasStoppedOnStep ? run.message : undefined}
          />
        )}
        {!isRunning && !isAwaitingStatus && (
          <ChatMessageBubble role="assistant" text={greeting} />
        )}
        {messages.map((message) => (
          <ChatMessageBubble
            key={message.id}
            role={message.role}
            text={message.text}
          />
        ))}
        {isAnswering && (
          <Typography variant="caption" color="text.secondary">
            {t('chat.answering')}
          </Typography>
        )}
        {!isAnswering && !isRunning && visibleSuggestions.length > 0 && (
          <StyledSuggestionsBox>
            {visibleSuggestions.map((suggestion) => (
              <Chip
                key={suggestion}
                label={suggestion}
                size="small"
                variant="outlined"
                onClick={() => submitQuestion(suggestion)}
              />
            ))}
          </StyledSuggestionsBox>
        )}
      </StyledMessageList>

      <StyledComposer onSubmit={handleSubmit}>
        <StyledQuestionField
          id="automatic-rostering-question"
          label={t('chat.questionLabel')}
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          size="small"
          autoComplete="off"
          disabled={isComposerDisabled}
        />
        <IconButton
          type="paper-plane"
          size="small"
          onClick={handleSubmit}
          disabled={isComposerDisabled || !question.trim()}
          aria-label={t('chat.send')}
        />
      </StyledComposer>
    </StyledPanel>
  );
};

export default AutomaticRosteringExplainPanel;
